import apiService from './api';

/**
 * Service for handling chat-related API calls
 */
const chatService = {
  /**
   * Get all conversations for the current user
   * @returns {Promise} Promise that resolves to an array of conversations
   */
  getConversations: async () => {
    try {
      const response = await apiService.get('/conversations');
      console.log('Raw API response for getConversations:', response);
      
      // If the response itself is an array, return it directly
      if (Array.isArray(response)) {
        return response;
      }
      
      // If the data property is an array, return that
      if (response && Array.isArray(response.data)) {
        return response.data;
      }
      
      // Otherwise return the original response
      return response;
    } catch (error) {
      console.error('Error in getConversations service:', error);
      throw error;
    }
  },

  /**
   * Get a conversation by ID
   * @param {string} id - The conversation ID
   * @returns {Promise} Promise that resolves to the conversation object
   */
  getConversation: async (id) => {
    try {
      const response = await apiService.get(`/conversations/${id}`);
      console.log(`Raw API response for getConversation(${id}):`, response);
      return response;
    } catch (error) {
      console.error(`Error in getConversation(${id}) service:`, error);
      throw error;
    }
  },

  /**
   * Create a new conversation
   * @param {Object} data - The conversation data
   * @returns {Promise} Promise that resolves to the created conversation
   */
  createConversation: async (data) => {
    try {
      const response = await apiService.post('/conversations', data);
      console.log('Raw API response for createConversation:', response);
      return response;
    } catch (error) {
      console.error('Error in createConversation service:', error);
      throw error;
    }
  },

  /**
   * Update a conversation
   * @param {string} id - The conversation ID
   * @param {Object} data - The updated conversation data
   * @returns {Promise} Promise that resolves to the updated conversation
   */
  updateConversation: async (id, data) => {
    try {
      const response = await apiService.put(`/conversations/${id}`, data);
      console.log(`Raw API response for updateConversation(${id}):`, response);
      return response;
    } catch (error) {
      console.error(`Error in updateConversation(${id}) service:`, error);
      throw error;
    }
  },

  /**
   * Delete a conversation
   * @param {string} id - The conversation ID
   * @returns {Promise} Promise that resolves when the conversation is deleted
   */
  deleteConversation: async (id) => {
    try {
      const response = await apiService.delete(`/conversations/${id}`);
      console.log(`Raw API response for deleteConversation(${id}):`, response);
      return response;
    } catch (error) {
      console.error(`Error in deleteConversation(${id}) service:`, error);
      throw error;
    }
  },

  /**
   * Get messages for a conversation
   * @param {string} conversationId - The conversation ID
   * @returns {Promise} Promise that resolves to an array of messages
   */
  getMessages: async (conversationId) => {
    try {
      const response = await apiService.get(`/conversations/${conversationId}/messages`);
      console.log(`Raw API response for getMessages(${conversationId}):`, response);
      
      // If the response itself is an array, return it directly
      if (Array.isArray(response)) {
        return response;
      }
      
      // If the data property is an array, return that
      if (response && Array.isArray(response.data)) {
        return response.data;
      }
      
      // Otherwise return the original response
      return response;
    } catch (error) {
      console.error(`Error in getMessages(${conversationId}) service:`, error);
      throw error;
    }
  },

  /**
   * Send a message and get AI response
   * @param {string} conversationId - The conversation ID
   * @param {string} prompt - The user's message
   * @param {Object} options - Optional parameters (model, temperature, etc.)
   * @returns {Promise} Promise that resolves to the response object
   */
  sendMessage: async (conversationId, prompt, options = {}) => {
    try {
      const response = await apiService.post('/messages/generate', {
        conversationId,
        prompt,
        ...options
      });
      console.log(`Raw API response for sendMessage(${conversationId}):`, response);
      
      // If the response itself is an array, return it directly
      if (Array.isArray(response)) {
        return { data: response };
      }
      
      // Otherwise return the original response
      return response;
    } catch (error) {
      console.error(`Error in sendMessage(${conversationId}) service:`, error);
      throw error;
    }
  },

  /**
   * Add a tag to a conversation
   * @param {string} conversationId - The conversation ID
   * @param {string} tag - The tag to add
   * @returns {Promise} Promise that resolves to the updated tags
   */
  addTag: async (conversationId, tag) => {
    try {
      const response = await apiService.post(`/conversations/${conversationId}/tags`, { tag });
      console.log(`Raw API response for addTag(${conversationId}, ${tag}):`, response);
      return response;
    } catch (error) {
      console.error(`Error in addTag(${conversationId}, ${tag}) service:`, error);
      throw error;
    }
  },

  /**
   * Remove a tag from a conversation
   * @param {string} conversationId - The conversation ID
   * @param {string} tag - The tag to remove
   * @returns {Promise} Promise that resolves when the tag is removed
   */
  removeTag: async (conversationId, tag) => {
    try {
      const response = await apiService.delete(`/conversations/${conversationId}/tags/${tag}`);
      console.log(`Raw API response for removeTag(${conversationId}, ${tag}):`, response);
      return response;
    } catch (error) {
      console.error(`Error in removeTag(${conversationId}, ${tag}) service:`, error);
      throw error;
    }
  },

  /**
   * Search conversations
   * @param {string} query - The search query
   * @returns {Promise} Promise that resolves to an array of matching conversations
   */
  searchConversations: async (query) => {
    try {
      const response = await apiService.get(`/conversations/search?query=${encodeURIComponent(query)}`);
      console.log(`Raw API response for searchConversations(${query}):`, response);
      return response;
    } catch (error) {
      console.error(`Error in searchConversations(${query}) service:`, error);
      throw error;
    }
  }
};

export default chatService; 